import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { SettingsContext } from '@/context/settings-context'
import { usePageData } from '../hooks'
import { useFormValidation } from '../hooks/useFormValidation'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { CharLimitedInput } from '@/components/common/CharLimitedInput'
import { Textarea } from '@/components/ui/textarea'
import { LeadFormQuestionsView } from './instantForm/LeadFormQuestionsView'
import { z } from '@/utils/myZodLite'
import { isEmpty } from 'lodash'

const Navigation = ({ goBack, handleNext }) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button className="tw:w-[156px] tw:!text-sm" onClick={handleNext}>
                Next
            </Button>
        </div>
    )
}

export const FormPrivacy = () => {
    const {
        goBack,
        goNext,
        getStepContentData,
        setStepContentData,
        dataForStepper,
        registerCustomNavigation,
        clearCustomNavigation,
    } = useWizard()
    const { connectedPublishers } = useMediaBuyingContext()
    const { theme } = useContext(SettingsContext)

    const { pageIcon, pageTitle, pageLink } = usePageData(connectedPublishers)

    // Form state
    const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('')
    const [linkText, setLinkText] = useState('')
    const [customNotices, setCustomNotices] = useState('')
    const [showCustomDisclaimer, setShowCustomDisclaimer] = useState(false)
    const [customDisclaimer, setCustomDisclaimer] = useState('')
    const [disclaimerText, setDisclaimerText] = useState('')
    const [consents, setConsents] = useState([{ id: crypto.randomUUID(), text: '', required: true }])

    // Validation schema
    const FormSchema = useMemo(
        () =>
            z.object(
                {
                    privacyPolicyUrl: z.string(),
                    showCustomDisclaimer: z.boolean(),
                    customDisclaimer: z.string(),
                    disclaimerText: z.string(),
                    consents: z.array(
                        z.object({
                            text: z.string(),
                            required: z.boolean(),
                        }),
                    ),
                },
                (data) => {
                    const errors = []

                    // Privacy policy URL is required
                    if (isEmpty(data.privacyPolicyUrl)) {
                        errors.push({ message: 'Link is required.', path: ['privacyPolicyUrl'] })
                    }

                    // If custom disclaimer is enabled, validate its fields
                    if (data.showCustomDisclaimer) {
                        if (isEmpty(data.customDisclaimer)) {
                            errors.push({ message: 'Custom disclaimer title is required.', path: ['customDisclaimer'] })
                        }
                        if (isEmpty(data.disclaimerText)) {
                            errors.push({ message: 'Disclaimer text is required.', path: ['disclaimerText'] })
                        }
                    }

                    // If we have consents, validate they have text
                    data.consents.forEach((consent, index) => {
                        if (isEmpty(consent.text)) {
                            errors.push({
                                message: 'Consent text is required.',
                                path: [`consent_${index}_text`],
                            })
                        }
                    })

                    return errors.length ? errors : true
                },
            ),
        [],
    )

    const { formErrors, validateForm: validate, hasErrors } = useFormValidation(FormSchema)

    const validateForm = useCallback(
        (silent = false) => {
            return validate(
                {
                    privacyPolicyUrl,
                    showCustomDisclaimer,
                    customDisclaimer,
                    disclaimerText,
                    consents,
                },
                silent,
            )
        },
        [validate, privacyPolicyUrl, showCustomDisclaimer, customDisclaimer, disclaimerText, consents],
    )

    useEffect(() => {
        registerCustomNavigation(
            <Navigation
                goBack={goBack}
                handleNext={() => {
                    const result = validate(
                        {
                            privacyPolicyUrl,
                            showCustomDisclaimer,
                            customDisclaimer,
                            disclaimerText,
                            consents,
                        },
                        false,
                    )

                    if (result) goNext()
                }}
            />,
        )

        return () => clearCustomNavigation()
    }, [privacyPolicyUrl, showCustomDisclaimer, customDisclaimer, disclaimerText, consents])

    // Load saved data
    useEffect(() => {
        const saved = getStepContentData() || {}
        setPrivacyPolicyUrl(saved.privacyPolicyUrl || '')
        setLinkText(
            saved.linkText || (pageTitle !== '' && pageTitle !== 'Page Name' && `${pageTitle}'s Privacy Policy`) || '',
        )
        setCustomNotices(saved.customNotices || '')
        setShowCustomDisclaimer(saved.showCustomDisclaimer || false)
        setCustomDisclaimer(saved.customDisclaimer || '')
        setDisclaimerText(saved.disclaimerText || '')
        setConsents(saved.consents || [])
    }, [pageLink, pageIcon, pageTitle])

    // Save data on changes
    useEffect(() => {
        setStepContentData({
            privacyPolicyUrl,
            linkText,
            customNotices,
            showCustomDisclaimer,
            customDisclaimer,
            disclaimerText,
            consents,
        })
    }, [privacyPolicyUrl, linkText, customNotices, customDisclaimer, disclaimerText, consents])

    // Generate privacy question for preview
    const privacyQuestion = useMemo(
        () => ({
            id: 'privacy-screen',
            privacy: true,
            privacyUrl: privacyPolicyUrl,
            linkText: linkText,
            facebookUrl: 'https://www.facebook.com/policy.php',
            customNotices,
            showCustomDisclaimer,
            customDisclaimer,
            disclaimerText,
            consents,
        }),
        [privacyPolicyUrl, linkText, customNotices, showCustomDisclaimer, customDisclaimer, disclaimerText, consents],
    )

    // Consent management functions
    const addConsent = (type) => {
        const newConsent = {
            id: crypto.randomUUID(),
            text: '',
            required: type === 'required',
        }
        setConsents([...consents, newConsent])
    }

    const updateConsent = (id, updates) => {
        setConsents(consents.map((c) => (c.id === id ? { ...c, ...updates } : c)))
    }

    const removeConsent = (id) => {
        if (consents.length > 1) {
            setConsents(consents.filter((c) => c.id !== id))
        }
    }

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="formPrivacy" />
            <div className={`d-flex h-100 ${theme} campaigns-container w-100`}>
                <div className="border-right-white-1 pt-4 tw:px-4">
                    <div className="tw:pb-4 tw:text-sm">
                        Because you will be collecting customer information, you need to include a link to your
                        company's privacy policy. Your link will appear with Facebook's default privacy disclaimer.
                    </div>

                    {/* Privacy Policy URL */}
                    <div className="mb-4">
                        <label className="form-label tw:text-base">Link</label>
                        <div className="tw:relative tw:pb-4">
                            <CharLimitedInput
                                value={privacyPolicyUrl}
                                onChange={(val) => {
                                    setPrivacyPolicyUrl(val)
                                    if (hasErrors) {
                                        validateForm(true)
                                    }
                                }}
                                placeholder="https://sunnytravels.com/privacy-policy"
                                className={formErrors['privacyPolicyUrl'] ? 'tw:!border-red-500' : ''}
                            />
                            {formErrors['privacyPolicyUrl'] && (
                                <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1 tw:absolute">
                                    {formErrors['privacyPolicyUrl']}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Link Text */}
                    <div className="mb-4">
                        <label className="form-label tw:text-base">
                            Link Text <span className="tw:text-blue-700 tw:text-xs tw:pl-2">Optional</span>
                        </label>
                        <CharLimitedInput
                            value={linkText}
                            onChange={setLinkText}
                            placeholder={`${pageTitle || 'Page Name'}'s Privacy Policy`}
                            maxChars={70}
                        />
                    </div>

                    {/* Custom Notices */}
                    <div className="mb-4">
                        <label className="form-label tw:text-base">
                            Custom Notices <span className="tw:text-blue-700 tw:text-xs tw:pl-2">Optional</span>
                        </label>
                        <div className="tw:text-sm tw:pb-2">
                            You can add additional notices such as marketing opt-ins and legal disclaimers to the
                            default Facebook privacy disclaimer.
                        </div>
                        <div className="tw:pb-4">
                            <Textarea
                                value={customNotices}
                                onChange={(e) => setCustomNotices(e.target.value)}
                                placeholder="Add disclaimer"
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Custom Disclaimer */}
                    {showCustomDisclaimer && (
                        <>
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="form-label tw:text-base">Custom Disclaimer</label>
                                    <button
                                        onClick={() => {
                                            setShowCustomDisclaimer(false)
                                            setCustomDisclaimer('')
                                            setDisclaimerText('')
                                            setConsents([])
                                            setStepContentData({
                                                privacyPolicyUrl: '',
                                                linkText: '',
                                                customNotices: '',
                                                showCustomDisclaimer: false,
                                                customDisclaimer: '',
                                                disclaimerText: '',
                                                consents: [],
                                            })
                                        }}
                                        className="tw:text-blue-400 tw:hover:text-red-500"
                                    >
                                        <i className="bi bi-trash" />
                                    </button>
                                </div>
                                <div className="tw:relative tw:pb-4">
                                    <CharLimitedInput
                                        value={customDisclaimer}
                                        onChange={(val) => {
                                            setCustomDisclaimer(val)
                                            if (hasErrors) {
                                                validateForm(true)
                                            }
                                        }}
                                        placeholder="Terms and Conditions for Sunny Travels"
                                        maxChars={60}
                                        className={formErrors['customDisclaimer'] ? 'tw:!border-red-500' : ''}
                                    />
                                    {formErrors['customDisclaimer'] && (
                                        <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1 tw:absolute">
                                            {formErrors['customDisclaimer']}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Disclaimer Text */}
                            <div className="mb-4">
                                <label className="form-label tw:text-base">Text</label>
                                <div className="tw:text-sm tw:pb-2">
                                    To add a link, highlight the words that you want to link.
                                </div>
                                <div className="tw:relative tw:pb-4">
                                    <Textarea
                                        value={disclaimerText}
                                        onChange={(e) => {
                                            setDisclaimerText(e.target.value)
                                            if (hasErrors) {
                                                validateForm(true)
                                            }
                                        }}
                                        placeholder="Add disclaimer"
                                        rows={3}
                                        className={formErrors['disclaimerText'] ? 'tw:!border-red-500' : ''}
                                    />
                                    {formErrors['disclaimerText'] && (
                                        <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1 tw:absolute">
                                            {formErrors['disclaimerText']}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Add Custom Disclaimer Button */}
                    {!showCustomDisclaimer && (
                        <div className="tw:mt-6 tw:pl-1 mb-4">
                            <button
                                type="button"
                                className="tw:bg-slate-900 tw:border tw:border-blue-500 tw:text-white tw:px-4 tw:py-2 tw:shadow-xl tw:flex tw:items-center tw:gap-1 tw:rounded-none"
                                onClick={() => {
                                    setShowCustomDisclaimer(true)
                                    setConsents([])
                                }}
                            >
                                <i className="bi bi-plus" />
                                Add Custom Disclaimer
                            </button>
                        </div>
                    )}

                    {/* Consents */}
                    {showCustomDisclaimer && (
                        <div className="mb-4">
                            {consents.map((consent, index) => (
                                <div key={consent.id} className="tw:border-bottom-white-1 tw:pb-4 tw:pt-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="form-label tw:text-base">Consent {index + 1}</label>
                                        <div className="tw:flex tw:items-center tw:gap-3">
                                            <input
                                                type="checkbox"
                                                checked={!consent.required}
                                                onChange={() =>
                                                    updateConsent(consent.id, { required: !consent.required })
                                                }
                                            />
                                            <label className="tw:flex tw:text-sm tw:text-white">
                                                <span>Optional</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="tw:relative tw:pb-4">
                                        <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
                                            <CharLimitedInput
                                                value={consent.text}
                                                onChange={(val) => {
                                                    updateConsent(consent.id, { text: val })
                                                    if (hasErrors) {
                                                        validateForm(true)
                                                    }
                                                }}
                                                placeholder="Required"
                                                className={
                                                    formErrors[`consent_${index}_text`] ? 'tw:!border-red-500' : ''
                                                }
                                            />
                                            {consents.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="tw:text-blue-400 tw:hover:text-red-500 tw:px-1"
                                                    onClick={() => removeConsent(consent.id)}
                                                >
                                                    <i className="bi bi-trash" />
                                                </button>
                                            )}
                                        </div>
                                        {formErrors[`consent_${index}_text`] && (
                                            <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1 tw:absolute">
                                                {formErrors[`consent_${index}_text`]}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Add Consent Button */}
                            <div className="tw:mt-6 tw:pl-1">
                                <button
                                    type="button"
                                    className="tw:bg-slate-900 tw:border tw:border-blue-500 tw:text-white tw:px-4 tw:py-2 tw:shadow-xl tw:flex tw:items-center tw:gap-1 tw:rounded-none"
                                    onClick={() => addConsent('required')}
                                >
                                    <i className="bi bi-plus" />
                                    Add Consent
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Phone Preview */}
                <div className="position-relative tw:p-6" style={{ width: 320, maxWidth: 320 }}>
                    <LeadFormQuestionsView
                        questions={[privacyQuestion]}
                        pageIcon={pageIcon}
                        pageTitle={pageTitle}
                        theme={theme}
                        hideSelectForm={true}
                    />
                </div>
            </div>
        </div>
    )
}
